import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner } from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, compressImageFile } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates } from "../fiscalYearHelper";
import { openAttachmentInNewTab } from "../attachmentHelper";
import ShareTransferContextPanel, {
  getShareMaxTransferable,
} from "../ShareTransferContextPanel";

const CustomInput = ({ label, name, value, onChange, error, readOnly, disabled, type = "text", children }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      className="no-drag"
      readOnly={readOnly}
      disabled={disabled}
    />
    {children}
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const EditMarkazShare = ({ setModal, transactionData, unitId, onSuccess }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId")) || unitId;

  const [loading, setLoading] = useState(false);
  const [shareContext, setShareContext] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const calculatedAmount = Number(shareContext?.minimumShare) || 0;

  const sourceAccountType =
    transactionData?.accountTypeCode === "cash_in_hand_account" ||
    transactionData?.accountTypeCode === "cash" ||
    transactionData?.sourceAccountType === "cash"
      ? "cash"
      : "amanat";
  const isCashSource = sourceAccountType === "cash";

  const existingFileId =
    transactionData?.fileId ||
    transactionData?.file_id ||
    transactionData?.FileId ||
    (Array.isArray(transactionData?.fileIds) && transactionData.fileIds[0]) ||
    (Array.isArray(transactionData?.file_ids) && transactionData.file_ids[0]) ||
    null;

  const [formData, setFormData] = useState({
    share_month: new Date(),
    processed_date: new Date(),
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});
  const [attachedFile, setAttachedFile] = useState(null);
  const parsedFormAmount = parseFloat(formData.amount);
  const isDeficit =
    calculatedAmount > 0 &&
    !isNaN(parsedFormAmount) &&
    parsedFormAmount > 0 &&
    parsedFormAmount < calculatedAmount;

  const parseMonthString = (monthStr) => {
    if (!monthStr) return null;
    const parts = monthStr.split(" ");
    if (parts.length === 2) {
      const monthIndex = new Date(`${parts[0]} 1, 2000`).getMonth();
      const year = parseInt(parts[1], 10);
      if (!isNaN(monthIndex) && !isNaN(year)) {
        return new Date(year, monthIndex, 1);
      }
    }
    return null;
  };

  const getShareMonthBounds = (shareMonthDate) => {
    const minDate = new Date(shareMonthDate.getFullYear(), shareMonthDate.getMonth(), 1);
    return { minDate, maxDate: new Date() };
  };

  const { minDate } = getShareMonthBounds(formData.share_month || new Date());
  const { minDate: fiscalMinDate, maxDate: latestShareMonthMaxDate } = getMinMaxDates();

  useEffect(() => {
    if (transactionData) {
      let shareMonthDate = parseMonthString(transactionData.shareMonth || transactionData.share_month);

      if (!shareMonthDate && transactionData.memo) {
        const months = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];
        const memoStr = transactionData.memo;
        const matchedMonth = months.find((m) => memoStr.toLowerCase().includes(m.toLowerCase()));
        const yearMatch = memoStr.match(/\b(20\d\d)\b/);
        if (matchedMonth && yearMatch) {
          const mIdx = months.indexOf(matchedMonth);
          shareMonthDate = new Date(parseInt(yearMatch[1], 10), mIdx, 1);
        }
      }

      if (!shareMonthDate && transactionData.date) {
        const d = new Date(transactionData.date);
        if (!isNaN(d.getTime())) {
          shareMonthDate = new Date(d.getFullYear(), d.getMonth(), 1);
        }
      }

      if (!shareMonthDate) {
        shareMonthDate = new Date();
      }

      const parsedProcessedDate = transactionData.date ? new Date(transactionData.date) : new Date();

      setFormData({
        share_month: isNaN(shareMonthDate.getTime()) ? new Date() : shareMonthDate,
        processed_date: isNaN(parsedProcessedDate.getTime()) ? new Date() : parsedProcessedDate,
        amount: transactionData.amount ? Math.abs(transactionData.amount).toString() : "",
        memo: transactionData.memo || "",
      });
    }
  }, [transactionData]);

  const formatLocalDateString = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!formData.share_month || !loggedInUnitId) return;

    setCalcLoading(true);
    const formattedDate = formatLocalDateString(formData.share_month);

    ApiFinanceLocal()
      .GetMarkazShareContext(loggedInUnitId, formattedDate)
      .then((res) => {
        setShareContext(res.data || {});
        setCalcLoading(false);
      })
      .catch((err) => {
        setCalcLoading(false);
      });
  }, [formData.share_month, loggedInUnitId]);

  const toggle = () => setModal(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "amount" && !isCashSource) {
      const nextAmount = parseFloat(value);
      if (!(calculatedAmount > 0 && nextAmount > 0 && nextAmount < calculatedAmount)) {
        setAttachedFile(null);
        setErrors((prev) => ({ ...prev, attachedFile: "" }));
      }
    }
  };

  const handleShareMonthChange = (date) => {
    const selectedShareMonth = date ?? new Date();
    const { minDate } = getShareMonthBounds(selectedShareMonth);

    let nextProcessedDate = formData.processed_date || new Date();
    if (nextProcessedDate < minDate) {
      nextProcessedDate = minDate;
    }

    setFormData((prev) => ({
      ...prev,
      share_month: selectedShareMonth,
      processed_date: nextProcessedDate,
    }));
    setErrors((prev) => ({ ...prev, share_month: "", processed_date: "" }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setAttachedFile(null);
      setErrors((prev) => ({
        ...prev,
        attachedFile: isCashSource
          ? (t("file_size_limit_1_5mb") || "File size must be 1.5MB or less.")
          : (t("markaz_share_justification_size_limit") || "File size must not exceed 2MB."),
      }));
      e.target.value = "";
    } else {
      setAttachedFile(processedFile);
      setErrors((prev) => ({ ...prev, attachedFile: "" }));
    }
  };

  const handleViewExistingAttachment = () => {
    if (openAttachmentInNewTab(transactionData)) {
      return;
    }
    if (!existingFileId) return;
    setLoadingView(true);
    ApiFinanceLocal()
      .ViewAttachmentByFileId(existingFileId)
      .then((res) => {
        const file = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: "Unable to view attachment file.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoadingView(false));
  };

  const validate = () => {
    const temp = {};
    let isValid = true;

    if (!formData.share_month) {
      temp.share_month = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.processed_date) {
      temp.processed_date = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      temp.amount = t("common_field_required_error");
      isValid = false;
    } else if (calcLoading) {
      temp.amount = "Calculating share amount, please wait...";
      isValid = false;
    } else {
      const parsedAmount = parseFloat(formData.amount);
      const maxTransferable = getShareMaxTransferable(shareContext, sourceAccountType);
      const totalInfaq = Number(shareContext?.totalInfaq) || 0;
      if (parsedAmount > totalInfaq) {
        temp.amount = t("share_amount_exceeds_total_infaq");
        isValid = false;
      } else if (parsedAmount > maxTransferable) {
        temp.amount = t("share_amount_exceeds_account_balance");
        isValid = false;
      }
    }

    if (isCashSource) {
      if (!existingFileId && !attachedFile) {
        temp.attachedFile = t("markaz_share_deposit_slip_required") || "Deposit slip / payment screenshot is required for Cash in Hand Markaz Share.";
        isValid = false;
      }
    } else if (isDeficit) {
      if (!attachedFile) {
        temp.attachedFile = t("markaz_share_justification_required");
        isValid = false;
      }
    }

    if (!formData.memo || !formData.memo.trim()) {
      temp.memo = t("common_field_required_error");
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validate()) {
      return;
    }

    const executeSubmit = () => {
      let finalMemo = formData.memo ? formData.memo.trim() : "";
      if (formData.share_month) {
        const monthName = formData.share_month.toLocaleString("en-US", { month: "long" });
        const year = formData.share_month.getFullYear();
        if (!finalMemo.toLowerCase().includes(monthName.toLowerCase())) {
          finalMemo = finalMemo ? `${finalMemo} (${monthName} ${year})` : `${monthName} ${year}`;
        }
      }

      const originalTransactionNo = transactionData.transactionNo || transactionData.transNo || transactionData.fundId || 0;
      const originalCodeId = transactionData.codeId || transactionData.code_id || transactionData.category?.value || transactionData.categoryCode || "";
      const originalAmount = String(transactionData.amount ? Math.abs(transactionData.amount) : "");
      const originalDate = formatLocalDateString(transactionData.date ? new Date(transactionData.date) : new Date());
      const originalMemo = transactionData.memo || "";

      const requiresForm = isCashSource || isDeficit || attachedFile;
      const request = requiresForm
        ? (() => {
            const payload = new FormData();
            payload.append("unitId", loggedInUnitId);
            payload.append("date", formatLocalDateString(formData.processed_date));
            payload.append("share_month", formatLocalDateString(formData.share_month));
            payload.append("amount", parseFloat(formData.amount));
            payload.append("memo", finalMemo);
            payload.append("isRequest", "true");
            payload.append("source_account_type", sourceAccountType);
            payload.append("account_type_code", transactionData.accountTypeCode || "");
            payload.append("transactionNo", originalTransactionNo);
            payload.append("originalCodeId", originalCodeId);
            payload.append("originalAmount", originalAmount);
            payload.append("originalDate", originalDate);
            payload.append("originalMemo", originalMemo);
            if (attachedFile) {
              payload.append("File", attachedFile);
            }
            return ApiFinanceLocal().RequestEditMarkazShareWithJustification(payload);
          })()
        : ApiFinanceLocal().RequestEditMarkazShare({
            transactionNo: originalTransactionNo,
            date: formatLocalDateString(formData.processed_date),
            share_month: formatLocalDateString(formData.share_month),
            amount: parseFloat(formData.amount),
            memo: finalMemo,
            unitId: loggedInUnitId,
            isRequest: true,
            source_account_type: sourceAccountType,
            original: {
              transactionNo: originalTransactionNo,
              codeId: originalCodeId,
              amount: originalAmount,
              date: originalDate,
              memo: originalMemo,
            },
          });

      setLoading(true);

      request
        .then((res) => {
          const data = res.data;
          if (data?.statusCode === 200) {
            let msgKey = data?.message || "finance_markaz_share_edit_requested";
            let msgText = t(msgKey);
            if (msgText === msgKey || !msgText) {
              msgText = "Markaz Share edit request submitted for approval.";
            }
            swal({
              title: t("common_success"),
              text: msgText,
              icon: "success",
              buttons: "OK",
            });
            setModal(false);
            if (onSuccess) onSuccess();
          } else {
            let errText = data?.message || "common_something_went_wrong";
            if (t(errText) !== errText) errText = t(errText);
            swal({
              title: t("common_error"),
              text: errText,
              icon: "error",
              buttons: "OK",
            });
          }
        })
        .catch((err) => {
          let rawErr = err?.response?.data?.message || err?.response?.data || err?.message || "common_something_went_wrong";
          if (typeof rawErr !== "string") rawErr = JSON.stringify(rawErr);
          if (t(rawErr) !== rawErr) rawErr = t(rawErr);
          swal({
            title: t("common_error"),
            text: rawErr,
            icon: "error",
            buttons: "OK",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (isDeficit) {
      swal({
        title: t("share_request_confirm_title"),
        text: t("funds_markaz_share_request_confirm_text"),
        icon: "warning",
        buttons: {
          cancel: t("share_request_confirm_btn_cancel"),
          confirm: t("share_request_confirm_btn_proceed"),
        },
      }).then((confirmed) => {
        if (confirmed) {
          executeSubmit();
        }
      });
      return;
    }

    executeSubmit();
  };

  return (
    <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
      <ModalHeader className="modal-title" tag="h4">
        <GetLanguageString props="edit_markaz_share" />
      </ModalHeader>

      {loading ? (
        <ModalBody className="text-center py-4">
          <LoadingSpinner />
        </ModalBody>
      ) : (
        <Form onSubmit={handleSubmit} noValidate>
          <ModalBody>
            <ShareTransferContextPanel
              context={shareContext}
              loading={calcLoading}
              minimumLabelKey="share_minimum_markaz"
              sourceAccountType={sourceAccountType}
            />
            <Row className="mb-3">
              <Col md="3">
                <FormGroup>
                  <Label className="col-form-label">
                    <GetLanguageString props="funds_share_month" />
                  </Label>
                  <DatePicker
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText="Select month"
                    className="form-control no-drag"
                    selected={formData.share_month}
                    onChange={handleShareMonthChange}
                    showMonthYearPicker
                    minDate={fiscalMinDate || undefined}
                    maxDate={latestShareMonthMaxDate || undefined}
                    dateFormat="MMMM yyyy"
                    disabled={true}
                  />
                  {errors.share_month && (
                    <h4 className="text-error no-drag">{errors.share_month}</h4>
                  )}
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label className="col-form-label">
                    <GetLanguageString props="funds_processed_date" />
                  </Label>
                  <DatePicker
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText="dd/mm/yyyy"
                    className="form-control no-drag"
                    selected={formData.processed_date}
                    minDate={minDate || undefined}
                    maxDate={new Date()}
                    onChange={(date) => handleChange("processed_date", date ?? new Date())}
                    dateFormat="dd/MM/yyyy"
                  />
                  {errors.processed_date && (
                    <h4 className="text-error no-drag">{errors.processed_date}</h4>
                  )}
                </FormGroup>
              </Col>

              <Col md="3">
                <CustomInput
                  name="amount"
                  label="funds_add_fund_amount"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  error={errors.amount}
                >
                  {calcLoading ? (
                    <small className="text-muted d-block mt-1">Calculating share...</small>
                  ) : (
                    isDeficit && (
                      <div
                        style={{
                          backgroundColor: "#FEF3C7",
                          border: "1px solid #F59E0B",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          marginTop: "6px",
                          color: "#92400E",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        <i className="fa fa-exclamation-triangle mr-1" style={{ color: "#D97706", marginRight: "4px" }}></i>
                        {t("markaz_share_deficit_warning", { amount: calculatedAmount.toLocaleString() })}
                      </div>
                    )
                  )}
                </CustomInput>
              </Col>

              <Col md="3">
                <CustomInput
                  name="memo"
                  label="transactions_memo"
                  type="text"
                  value={formData.memo}
                  onChange={(e) => handleChange("memo", e.target.value)}
                  error={errors.memo}
                />
              </Col>
            </Row>

            {existingFileId && (
              <Row className="mb-3">
                <Col md="12">
                  <div
                    className="d-flex align-items-center justify-content-between p-3 rounded"
                    style={{
                      backgroundColor: "#F0F9FF",
                      border: "1px solid #BAE6FD",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa fa-paperclip text-info" style={{ fontSize: "16px" }}></i>
                      <div>
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#0369A1" }}>
                          {isCashSource
                            ? (t("deposit_slip_already_attached") || "Deposit slip already attached")
                            : (t("markaz_share_justification_attachment") || "Justification attachment already attached")}
                        </span>
                        <div style={{ fontSize: "11px", color: "#0284C7" }}>
                          {t("uploading_new_file_replaces_current_notice") || "Uploading a new file will replace this document upon approval."}
                        </div>
                      </div>
                    </div>
                    <Button
                      color="white"
                      size="sm"
                      style={{
                        padding: "6px 14px",
                        fontSize: "12px",
                        fontWeight: "500",
                        borderRadius: "6px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        border: "1px solid #D1D5DB",
                      }}
                      disabled={loadingView}
                      onClick={handleViewExistingAttachment}
                      type="button"
                    >
                      {loadingView ? (
                        <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                      ) : (
                        <i className="fa fa-eye text-primary"></i>
                      )}
                      <GetLanguageString props="member_add_rafiq_request_view_document" />
                    </Button>
                  </div>
                </Col>
              </Row>
            )}

            {(isCashSource || isDeficit) && (
              <Row className="mt-2">
                <Col md="6">
                  <div
                    className="no-drag"
                    style={{
                      backgroundColor: isCashSource ? "#F0FDF4" : "#FEF3C7",
                      border: isCashSource ? "1px solid #86EFAC" : "1px solid #F59E0B",
                      borderRadius: "8px",
                      padding: "12px 14px",
                    }}
                  >
                    <FormGroup className="mb-0">
                      <Label
                        className="col-form-label"
                        style={{
                          color: isCashSource ? "#166534" : "#92400E",
                          fontWeight: 700,
                        }}
                      >
                        <i
                          className="fa fa-paperclip"
                          style={{
                            marginRight: "6px",
                            color: isCashSource ? "#16A34A" : "#D97706",
                          }}
                        ></i>
                        {isCashSource
                          ? (existingFileId
                              ? (t("optional_replaces_existing") || "Optional - replaces existing document")
                              : (t("markaz_share_deposit_slip_attachment") || "Deposit Slip / Payment Screenshot (Required, Max 1.5MB)"))
                          : <GetLanguageString props="markaz_share_justification_attachment" />}
                      </Label>
                      <Input
                        type="file"
                        accept="image/*,application/pdf"
                        multiple={false}
                        name="file"
                        className="form-control"
                        onChange={handleFileChange}
                      />
                      {errors.attachedFile && (
                        <h4 className="text-error mt-1">{errors.attachedFile}</h4>
                      )}
                      {attachedFile && (
                        <div
                          className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                          style={{
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #CBD5E1",
                            fontSize: "12px",
                            color: "#334155",
                          }}
                        >
                          <div
                            className="d-flex align-items-center gap-2 text-truncate"
                            style={{ maxWidth: "80%" }}
                          >
                            <i className="fa fa-file text-success"></i>
                            <span className="text-truncate fw-bold">{attachedFile.name}</span>
                            <span className="badge bg-light text-muted border px-1">
                              {(attachedFile.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <Button
                            color="link"
                            size="sm"
                            className="text-danger p-0 ms-2 text-decoration-none"
                            onClick={() => setAttachedFile(null)}
                            title={t("remove_file") || "Remove file"}
                            type="button"
                          >
                            <i className="fa fa-times"></i>
                          </Button>
                        </div>
                      )}
                    </FormGroup>
                  </div>
                </Col>
              </Row>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              color="white"
              className={GetCustomStyles().btn_style}
              onClick={toggle}
            >
              <GetLanguageString props="common_cancel" />
            </Button>
            <Button
              color="primary"
              className={GetCustomStyles().btn_style}
              disabled={loading || calcLoading}
              type="submit"
            >
              <GetLanguageString props="common_submit" />
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Modal>
  );
};

export default EditMarkazShare;
